/**
 * Universal Section resolution for the compare view.
 *
 * The universal→region section map lives in ONE place: the backend's
 * `section_concept_mapping` (+ `section_concept_notes`), served by
 * `GET /api/section-concepts` — the same map CEPI's `read_label_section`
 * resolves through. Nothing here hardcodes a concept, a label, or a region:
 * adding China, Korea or an 18th universal section must be a backend seed
 * change only.
 *
 * See docs/plans/2026-09-22-universal-section-selector.md.
 */

import type { SectionConcept } from '../types';

/** One universal section and its per-region sections/notes, exactly as
 *  `/api/section-concepts` serves it. */
export type ConceptEntry = SectionConcept;

export interface PanelResolution {
  /** Sections to SHOW, primary first. `null` = show everything (never `[]`). */
  sections: string[] | null;
  /** Section a PDF panel should scroll to, or null to stay put. */
  scrollTo: string | null;
  /** Prose to surface in the panel header, or null. */
  note: string | null;
  /** True when this region has no section for the concept. */
  notApplicable: boolean;
}

export function universalVocabulary(concepts: ConceptEntry[]): ConceptEntry[] {
  return concepts
    .filter((c) => c.universal_order !== null && c.universal_order !== undefined)
    .sort((a, b) => (a.universal_order as number) - (b.universal_order as number));
}

/**
 * Resolve one universal section for one panel's region.
 *
 * `sections: null` means "show everything, do not move" — deliberately NOT `[]`,
 * which the compare column would read as a filter matching nothing and blank the
 * panel. A region with no section for the concept keeps its document intact and
 * surfaces `note` instead.
 */
export function resolveForRegion(
  concept: ConceptEntry | null,
  region: string,
): PanelResolution {
  if (!concept) {
    return { sections: null, scrollTo: null, note: null, notApplicable: false };
  }
  const sections = concept.sections?.[region];
  const note = concept.notes?.[region] ?? null;
  if (!sections || sections.length === 0) {
    return { sections: null, scrollTo: null, note, notApplicable: true };
  }
  // Primary first (the backend orders by display_order), so a PDF panel scrolls
  // to the section the author nominated rather than whichever sorts first.
  return { sections, scrollTo: sections[0] ?? null, note, notApplicable: false };
}

/**
 * Expand each concept section to its whole family among the sections a document
 * actually has: "5" pulls 5.1, 5.2, 5.10 — but never 51 or 5A.
 *
 * FDA labels number hierarchically and CLAUDE.md requires every section match to
 * include subsections; the dot is what makes it a subsection rather than a
 * different section that merely starts with the same digits.
 */
export function expandSectionFamilies(
  conceptSections: string[],
  present: string[],
): string[] {
  return present.filter((n) =>
    conceptSections.some((c) => n === c || n.startsWith(c + '.')),
  );
}

/**
 * Resolve the active concepts to ONE column's region and UNION their sections.
 * `scroll` is the first pick's primary (the PDF scroll target); `isUniversal`
 * is true if any pick is a real universal section (vs a Cogni one-region
 * pseudo-concept). Shared by the column and the export context so they cannot
 * drift.
 */
export function resolveColumnConcepts(
  concepts: ConceptEntry[],
  region: string,
): { nums: string[] | undefined; scroll: string | undefined; isUniversal: boolean } {
  const nums: string[] = [];
  let scroll: string | undefined;
  let isUniversal = false;
  for (const c of concepts) {
    const r = resolveForRegion(c, region);
    if (c.universal_order != null) isUniversal = true;
    if (!scroll && r.scrollTo) scroll = r.scrollTo;
    for (const s of r.sections ?? []) if (!nums.includes(s)) nums.push(s);
  }
  return { nums: nums.length ? nums : undefined, scroll, isUniversal };
}

/**
 * The concept sections an EXPORT of one column should narrow to when it has no
 * manual pick: exactly what the column's `narrowedSections` / `pdfEff` use.
 * `undefined` = all (no concept here, or a JP column whose only concept is
 * Cogni evidence, which never filters).
 */
export function exportConceptSections(
  concepts: ConceptEntry[],
  region: string,
): string[] | undefined {
  const { nums, isUniversal } = resolveColumnConcepts(concepts, region);
  if (region.toUpperCase() === 'JP' && !isUniversal) return undefined;
  return nums;
}

/**
 * Which sections a compare panel should SHOW, given the active concept.
 *
 * Precedence is manual > universal > all, and the result is `undefined`
 * ("everything") rather than `[]` whenever narrowing would empty the panel.
 *
 * JP is the one asymmetry, and it is about INTENT, not about Japanese: a
 * universal pick is the user explicitly asking to see that section, so a JP
 * panel narrows like every other panel. The Cogni evidence path builds a
 * one-region pseudo-concept to point at the sentence it cited — there the JP
 * panel must keep the whole bilingual label and merely scroll, so that path
 * still never filters.
 */
export function narrowedSections(opts: {
  /** Per-column manual selection; wins outright when present. */
  manual?: string[] | undefined;
  /** This region's sections for the active concept (resolveForRegion). */
  conceptSections: string[] | null;
  /** True for a Universal Section pick, false for Cogni evidence. */
  isUniversal: boolean;
  isJp: boolean;
  /** Section numbers this document actually has. */
  present: string[];
}): string[] | undefined {
  const { manual, conceptSections, isUniversal, isJp, present } = opts;
  if (manual !== undefined) return manual;
  if (!conceptSections || conceptSections.length === 0) return undefined;
  if (isJp && !isUniversal) return undefined;
  const fam = expandSectionFamilies(conceptSections, present);
  return fam.length ? fam : undefined;
}
